import type { MatchReplaceRule } from '../store/proxy-store';
import type { HttpRequest } from '../types/http-request';

function headerValue(
  headers: Record<string, string | string[]>,
  name: string,
): string {
  const direct = headers[name];
  if (direct !== undefined) {
    return Array.isArray(direct) ? (direct[0] ?? '') : direct;
  }

  const lowerName = name.toLowerCase();
  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() === lowerName) {
      return Array.isArray(value) ? (value[0] ?? '') : value;
    }
  }

  return '';
}

export function testMatchReplacePattern({
  value,
  pattern,
  isRegex,
  caseSensitive,
}: {
  value: string;
  pattern: string;
  isRegex: boolean;
  caseSensitive: boolean;
}): boolean {
  if (!pattern) {
    return false;
  }

  try {
    if (isRegex) {
      const flags = caseSensitive ? 'g' : 'gi';
      const regex = new RegExp(pattern, flags);
      regex.lastIndex = 0;
      return regex.test(value);
    }

    const searchValue = caseSensitive ? value : value.toLowerCase();
    const searchPattern = caseSensitive ? pattern : pattern.toLowerCase();
    return searchValue.includes(searchPattern);
  } catch {
    return false;
  }
}

export function matchesMatchReplaceRuleConditions({
  rule,
  request,
}: {
  rule: MatchReplaceRule;
  request: HttpRequest;
}): boolean {
  const { conditions } = rule;

  if (!conditions) {
    return true;
  }

  if (conditions.urlPattern) {
    const urlMatches = testMatchReplacePattern({
      value: request.url,
      pattern: conditions.urlPattern,
      isRegex: false,
      caseSensitive: true,
    });
    if (!urlMatches) {
      return false;
    }
  }

  if (conditions.methods && conditions.methods.length > 0) {
    if (!conditions.methods.includes(request.method)) {
      return false;
    }
  }

  if (conditions.statusCodes && conditions.statusCodes.length > 0) {
    if (!request.status || !conditions.statusCodes.includes(request.status)) {
      return false;
    }
  }

  if (conditions.contentTypes && conditions.contentTypes.length > 0) {
    const requestContentType = headerValue(request.headers, 'content-type');
    const responseContentType = request.responseHeaders
      ? headerValue(request.responseHeaders, 'content-type')
      : '';
    const contentType = (
      requestContentType || responseContentType
    ).toLowerCase();
    const matchesContentType = conditions.contentTypes.some(
      (contentTypePattern) =>
        contentType.includes(contentTypePattern.toLowerCase()),
    );
    if (!matchesContentType) {
      return false;
    }
  }

  return true;
}

export function filterRequestsForMatchReplaceRule({
  requests,
  rule,
}: {
  requests: HttpRequest[];
  rule: MatchReplaceRule | null;
}): HttpRequest[] {
  if (!rule) {
    return requests;
  }

  return requests.filter((request) =>
    matchesMatchReplaceRuleConditions({ rule, request }),
  );
}
